import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-links.css';
import { Attribute } from '../../decorators/attribute.decorator';
import type {
    UILinkLayout,
    UILinksDirection,
    UILinksAlignment,
} from './ui-links.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UILinks
 * @element ui-links
 * @classdesc `ui-links` acts as a container for multiple `ui-link` elements.
 * @property {UILinksDirection} [direction="horizontal"] {@attr direction} Direction of the link container.
 *  {@desc horizontal: Links are placed next to each other in a row. Default.}
 *  {@desc vertical: Links are stacked top to bottom.}
 * @property {UILinkLayout} [layout] {@attr layout} Layout style to propagate to child `ui-link` elements that don't have one set.
 * @property {UILinksAlignment} [alignment="left"] {@attr alignment} Alignment of the link container.
 *  {@desc left: Links are aligned to the left. Default.}
 *  {@desc center: Links are centered within the container.}
 *  {@desc stretch: Links are stretched to fill the container.}
 * @slot
 * @example
 * <ui-links layout="primary">
 *  <ui-link label="Link 1" href="#link-1"></ui-link>
 *  <ui-link label="Link 2" href="#link-2"></ui-link>
 * </ui-links>
 * @example
 * <ui-links direction="horizontal">
 *  <ui-link label="Link 1" href="#link-1" layout="icon" glyph="redesign-circle-eye"></ui-link>
 *  <ui-link label="Link 2" href="#link-2" layout="primary"></ui-link>
 * </ui-links>
 */

@Component({
    tag: 'ui-links',
    styles,
})
export class UILinks extends UIElement {
    @Attribute() layout?: UILinkLayout;
    @Attribute(String) direction: UILinksDirection = 'horizontal';
    @Attribute(String) alignment: UILinksAlignment = 'left';

    /**
     * Sets the 'layout' attribute on child 'ui-link' elements that don't already have one.
     * If no layout is set on `ui-links`, child links keep their own layout attributes.
     * @private
     */
    setLayoutOnLinks(): void {
        if (!this.layout) {
            return;
        }
        const links = this.querySelectorAll('ui-link');
        links.forEach((link) => {
            if (!link.hasAttribute('layout')) {
                link.setAttribute('layout', this.layout!);
            }
        });
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.updateElement({
            classList: { 'ui-links': true },
            attributes: { role: 'group' },
        });

        this.setLayoutOnLinks();
    }
}
